import { useGetQuizWithAttempt } from '@repo/api/quiz/useGetQuizWithAttempt';
import { ErrorCodes } from '@repo/lib/errorCodes';
import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { QuizSection } from '@repo/types/quiz';
import { isAxiosError } from 'axios';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useQuizParams } from '@/hooks/quiz/useQuizParams';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useQuizResolverStore } from '@/store/quiz-store/quizResolverStore';

export function useQuizResolverData() {
  const { id, attemptId, sectionNumber } = useQuizParams();
  const syncWithAttemptData = useQuizResolverStore((state) => state.syncWithAttemptData);
  const navigate = useNavigate();

  const redirect = (quizId: string, attemptId: string, destination: number | null) => {
    if (destination !== null) {
      const params = new URLSearchParams(window.location.search);
      const current = Number(params.get('n') ?? '0');
      if (destination !== current) {
        navigate(`/quizzes/${quizId}/attempt/${attemptId}?n=${destination}`, {
          replace: true,
        });
      }
    } else {
      navigate(`/quizzes`, { replace: true });
    }
  };

  const localTimestamp = useQuizResolverStore((state) => state.localTimestamp);
  const getQuizQuery = useGetQuizWithAttempt(id, attemptId, sectionNumber, redirect, syncWithAttemptData);

  const totalSections = useMemo<number>(() => {
    return getQuizQuery.data?.quiz.content.length || 0;
  }, [getQuizQuery.data]);

  const currentSection = useMemo<QuizSection | undefined>(
    () => getQuizQuery.data?.quiz.content[sectionNumber <= totalSections ? sectionNumber : totalSections - 1],
    [getQuizQuery.data, sectionNumber, totalSections]
  );

  const totalQuestions = useMemo<number>(() => {
    return (
      getQuizQuery.data?.quiz.content.reduce((total, section) => {
        return total + (section.questions.length || 0);
      }, 0) || 0
    );
  }, [getQuizQuery.data]);

  const useSectionTimer = useMemo<boolean>(() => {
    return getQuizQuery.data?.quiz.content.every((section) => section.time !== undefined) ?? false;
  }, [getQuizQuery.data, sectionNumber]);

  useEffect(() => {
    const isError: boolean = getQuizQuery.isError;
    const error: Error | null = getQuizQuery.error;
    if (isError) {
      if (isAxiosError(error) && error.response?.data.code === ErrorCodes.FORBIDDEN.QUIZ.NO_VALID_ATTEMPT) {
        navigate('/quizzes', { replace: true });
      }

      toasterMessageHandler(quizErrorMessages, error);
    }
  }, [getQuizQuery.isError, getQuizQuery.error, navigate]);

  return {
    id,
    attemptId,
    data: getQuizQuery.data,
    isPending: getQuizQuery.isPending,
    isError: getQuizQuery.isError,
    isSuccess: getQuizQuery.isSuccess,
    sectionIndex: sectionNumber,
    currentSection: currentSection,
    totalSections: totalSections,
    totalQuestions: totalQuestions,
    isSectionTimer: useSectionTimer,
    isQuizTimer: Boolean(getQuizQuery.data?.quiz.time),
    localTimestamp: localTimestamp,
    freeNavigation: getQuizQuery.data?.quiz.freeNavigation || false,
    instantMark: getQuizQuery.data?.quiz.instantMark || false,
  };
}
